import * as React from 'react';
import { graphql } from 'react-apollo';

import { compose, withProps } from 'recompose';

import connectToRouter from 'js/lib/connectToRouter';
import User from 'js/lib/user';

import EnterpriseProgramBySlugQuery from 'bundles/enterprise-legacy-learner-home/queries/EnterpriseProgramBySlugQuery';
import type {
  EnterpriseProgramBySlugQueryResult,
  EnterpriseProgramBySlugQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/queries/EnterpriseProgramBySlugQuery';
import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';
import type { EnterpriseProgramsV1 } from 'bundles/naptimejs/resources/__generated__/EnterpriseProgramsV1';
import { EnterpriseHomePageQuery } from 'bundles/program-home/components/enterprise-home/EnterpriseHomePageQuery';
import type {
  EnterpriseHomePageQuery as EnterpriseHomePageQueryType,
  EnterpriseHomePageQueryVariables,
  EnterpriseHomePageQuery_EnterpriseLearnerSummariesV1Resource_byUserIdAndThirdPartyOrgSlug_elements_enterprisePrograms_elements as EnterpriseProgram,
} from 'bundles/program-home/components/enterprise-home/__generated__/EnterpriseHomePageQuery';
import CareerAcademyVariantContext from 'bundles/unified-career-academy/contexts/CareerAcademyVariantContext';
import DomainsContext from 'bundles/unified-career-academy/contexts/DomainsContext';
import EventingMetadataContext from 'bundles/unified-career-academy/contexts/EventingMetadataContext';
import PageContentContext from 'bundles/unified-career-academy/contexts/PageContentContext';
import withEnterpriseContentfulSlug from 'bundles/unified-career-academy/hocs/withEnterpriseContentfulSlug';
import withEnterpriseProgramDomains from 'bundles/unified-career-academy/hocs/withEnterpriseProgramDomains';
import withUnifiedCareerAcademyContentfulData from 'bundles/unified-career-academy/hocs/withUnifiedCareerAcademyContentfulData';
import type { PropsFromContentful } from 'bundles/unified-career-academy/hocs/withUnifiedCareerAcademyContentfulData';
import type {
  ProgramBySlugQuery_ProgramsV1Resource_slug_elements_curriculum as Curriculum,
  ProgramBySlugQuery as ProgramBySlugQueryResult,
  ProgramBySlugQueryVariables,
} from 'bundles/unified-career-academy/queries/__generated__/ProgramBySlugQuery';
import {
  ProgramBySlugQuery,
  getProgramCareerAcademyVariantQueryGraphQl,
} from 'bundles/unified-career-academy/queries/enterpriseQueries';
import type { DomainWithSubdomain } from 'bundles/unified-career-academy/types/enterprise';
import type { PropCareerAcademyVariant } from 'bundles/unified-career-academy/utils/helpers';
import EnterprisePageDataProvider from 'bundles/unified-common/providers/EnterprisePageDataProvider';
import EnterprisePermittedProductsDataProvider from 'bundles/unified-common/providers/EnterprisePermittedProductsDataProvider';

import AllEnterpriseProgramsContext from '../contexts/AllEnterpriseProgramsContext';
import ProgramCurriculumContext from '../contexts/ProgramCurriculumContext';

type PropsFromCaller = {
  children: React.ReactNode;
};

type PropsFromRouter = {
  programSlug: string;
};

type PropsFromProgram = {
  program?: EnterpriseProgramsV1;
};

type PropsFromProgramBySlugQuery = {
  curriculum?: Curriculum;
};

type PropsFromProgramDomains = {
  domains?: Array<DomainWithSubdomain>;
};

type PropsFromSummariesQuery = {
  enterprisePrograms: EnterpriseProgram[];
};

type PropsFromWithProps = {
  userId: string;
};

type PropsToComponent = PropsFromCaller &
  PropsFromProgram &
  PropsFromProgramBySlugQuery &
  PropsFromProgramDomains &
  PropsFromContentful &
  PropsFromSummariesQuery &
  PropCareerAcademyVariant;

const EnterpriseDataProvider = ({
  children,
  pageContent,
  program,
  careerAcademyVariant,
  isCareerAcademyVariantLoading,
  curriculum,
  enterprisePrograms,
  domains,
}: PropsToComponent) => {
  return (
    <PageContentContext.Provider value={pageContent}>
      <EnterprisePageDataProvider>
        <ProgramCurriculumContext.Provider value={curriculum}>
          <CareerAcademyVariantContext.Provider
            value={{
              careerAcademyVariant,
              isCareerAcademyVariantLoading,
            }}
          >
            <EnterprisePermittedProductsDataProvider>
              <AllEnterpriseProgramsContext.Provider value={enterprisePrograms}>
                <DomainsContext.Provider value={domains}>
                  <EventingMetadataContext.Provider
                    value={{
                      program: {
                        id: program?.id,
                        slug: program?.metadata.slug,
                        name: program?.metadata.name,
                      },
                    }}
                  >
                    {children}
                  </EventingMetadataContext.Provider>
                </DomainsContext.Provider>
              </AllEnterpriseProgramsContext.Provider>
            </EnterprisePermittedProductsDataProvider>
          </CareerAcademyVariantContext.Provider>
        </ProgramCurriculumContext.Provider>
      </EnterprisePageDataProvider>
    </PageContentContext.Provider>
  );
};

export default compose<PropsToComponent, PropsFromCaller>(
  connectToRouter((router) => {
    return {
      programSlug: router.params.programSlug,
    };
  }),
  graphql<PropsFromRouter, EnterpriseProgramBySlugQueryResult, EnterpriseProgramBySlugQueryVariables, PropsFromProgram>(
    EnterpriseProgramBySlugQuery,
    {
      options: ({ programSlug }) => ({ variables: { programSlug } }),
      props: ({ data }) => {
        const programs = data?.EnterpriseProgramsV1?.slug?.elements;
        return {
          program: programs ? programs[0] : undefined,
          thirdPartyOrgSlug: data?.EnterpriseProgramsV1?.slug?.linked?.thirdPartyOrganizationsV1?.[0]?.slug,
        };
      },
    }
  ),
  // Steven: Temp solution since logged-out users will run into permission issues with ProgramCurriculumsV1
  // ProgramsV1 is deprecated resource so we should get rid of this as part of CA Scaling
  graphql<PropsFromRouter, ProgramBySlugQueryResult, ProgramBySlugQueryVariables, PropsFromProgramBySlugQuery>(
    ProgramBySlugQuery,
    {
      options: ({ programSlug }) => ({ variables: { slug: programSlug } }),
      props: ({ data }) => {
        const curriculum = data?.ProgramsV1Resource?.slug?.elements[0]?.curriculum as Curriculum;

        return {
          curriculum,
        };
      },
    }
  ),
  withProps<PropsFromWithProps, {}>(() => ({
    userId: User.get().id?.toString(),
  })),
  graphql<
    PropsFromProgram & PropsFromWithProps & { thirdPartyOrgSlug?: string },
    EnterpriseHomePageQueryType,
    EnterpriseHomePageQueryVariables,
    PropsFromSummariesQuery
  >(EnterpriseHomePageQuery, {
    skip: ({ thirdPartyOrgSlug, userId }) => !thirdPartyOrgSlug || !userId,
    // @ts-expect-error thirdPartyOrgSlug asserted in above skip.
    options: ({ thirdPartyOrgSlug, userId }) => ({
      variables: {
        userId,
        thirdPartyOrgSlug,
      },
    }),
    props: ({ data }) => {
      const enterprisePrograms =
        data?.EnterpriseLearnerSummariesV1Resource?.byUserIdAndThirdPartyOrgSlug?.elements?.[0]?.enterprisePrograms
          ?.elements;
      return {
        enterprisePrograms: filterExistsOrDefault(enterprisePrograms),
      };
    },
  }),
  getProgramCareerAcademyVariantQueryGraphQl<{ program: EnterpriseProgramsV1 }>(),
  withEnterpriseProgramDomains,
  withEnterpriseContentfulSlug(),
  withUnifiedCareerAcademyContentfulData()
)(EnterpriseDataProvider);
